import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { platformAPI } from "../services/api";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { groupByCategory, plural } from "../utils/grouping";
import SelectBox from "./SelectBox";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:3010/api";

// Must match LIMIT in ProductTemplate.browseShopProducts
const RESULT_LIMIT = 500;

/**
 * "Add from existing shop products": every sellable product on the platform
 * (house recipes excluded server-side), grouped by category. Each product can
 * be added on its own, ticked and added together, or the whole filtered list
 * can be added at once — the filters (search / category / shop) decide what
 * "all" means.
 */
export default function ShopProductBrowser({ templateId, startOpen, onAdded }) {
  const [open, setOpen] = useState(startOpen);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [collapsedCats, setCollapsedCats] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  // Only the newest search may fill the list: a slow answer to an earlier
  // filter would otherwise replace it, and "Add all shown" would add a list
  // that is not the one on screen
  const latest = useRef(0);

  useEffect(() => {
    platformAPI.listShops().then(({ data }) => setShops(data));
  }, []);

  const load = useCallback(async () => {
    const request = ++latest.current;
    setLoading(true);

    let data;
    try {
      ({ data } = await platformAPI.browseShopProducts({
        search: search || undefined,
        category: category || undefined,
        shop_id: shopId || undefined,
        exclude_template_id: templateId,
      }));
    } finally {
      if (request === latest.current) setLoading(false);
    }
    if (request !== latest.current) return;

    setResults(data);
    // Forget ticks on anything no longer listed or already added
    const addable = new Set(data.filter((p) => !p.already_added).map((p) => p.id));
    setSelected((prev) => new Set([...prev].filter((id) => addable.has(id))));
  }, [search, category, shopId, templateId]);

  useEffect(() => {
    if (open) load().catch(() => setMessage("Failed to load products"));
  }, [open, load]);

  const addable = useMemo(() => results.filter((p) => !p.already_added), [results]);
  const sections = useMemo(() => groupByCategory(results, (p) => p.category), [results]);

  const add = async (ids) => {
    if (ids.length === 0) return;
    setBusy(true);
    setMessage("");
    try {
      const { data } = await platformAPI.addTemplateItemsFromProducts(templateId, ids);
      setMessage(
        `Added ${plural(data.added, "product")}` +
          (data.skipped ? ` (${data.skipped} already in the template or not eligible)` : "")
      );
      setSelected(new Set());
      await load();
      onAdded();
    } catch (err) {
      setMessage(err.response?.data?.error || "Failed to add products");
    } finally {
      setBusy(false);
    }
  };

  const setMany = (ids, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const toggleCat = (cat) =>
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const allTicked = addable.length > 0 && selected.size === addable.length;

  return (
    <div className="bg-white rounded-xl shadow">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-start"
      >
        <span className={`text-gray-400 text-xs transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <h3 className="font-semibold text-gray-800">Add from existing shop products</h3>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <input
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {PRODUCT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
            >
              <option value="">All shops</option>
              {shops.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-sm">
            <label className="flex items-center gap-2">
              <SelectBox
                checked={allTicked}
                indeterminate={selected.size > 0}
                onChange={(on) => setMany(addable.map((p) => p.id), on)}
                label="Select every product listed"
              />
              {selected.size > 0 ? `${selected.size} selected` : "Select all shown"}
            </label>
            <button
              onClick={() => add(Array.from(selected))}
              disabled={busy || loading || selected.size === 0}
              className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              Add selected{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            <button
              onClick={() => add(addable.map((p) => p.id))}
              disabled={busy || loading || addable.length === 0}
              className="px-3 py-1.5 border border-primary-600 text-primary-700 hover:bg-primary-50 rounded-lg font-semibold disabled:opacity-50"
            >
              Add all {addable.length > 0 ? `${addable.length} ` : ""}shown
            </button>
            {message && <span className="text-gray-600">{message}</span>}
          </div>

          <div className="max-h-[28rem] overflow-y-auto border border-gray-100 rounded-lg">
            {sections.map(({ category: cat, entries }) => {
              const catAddable = entries.filter((p) => !p.already_added);
              const catTicked = catAddable.filter((p) => selected.has(p.id)).length;
              const collapsed = collapsedCats.has(cat);

              return (
                <div key={cat}>
                  <div className="sticky top-0 z-10 flex items-center gap-3 bg-gray-50 border-b border-gray-100 px-3 py-1.5">
                    <SelectBox
                      checked={catAddable.length > 0 && catTicked === catAddable.length}
                      indeterminate={catTicked > 0}
                      onChange={(on) => setMany(catAddable.map((p) => p.id), on)}
                      label={`Select every ${cat}`}
                    />
                    <button
                      type="button"
                      onClick={() => toggleCat(cat)}
                      aria-expanded={!collapsed}
                      className="flex flex-1 items-center gap-2 text-start"
                    >
                      <span className={`text-gray-400 text-xs transition-transform ${collapsed ? "" : "rotate-90"}`}>
                        ▶
                      </span>
                      <span className="font-semibold text-gray-700 capitalize">{cat}</span>
                      <span className="text-xs text-gray-500">
                        {entries.length}
                        {catAddable.length !== entries.length && ` · ${catAddable.length} to add`}
                      </span>
                    </button>
                    <button
                      onClick={() => add(catAddable.map((p) => p.id))}
                      disabled={busy || loading || catAddable.length === 0}
                      className="text-xs text-primary-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                    >
                      Add all {cat}
                    </button>
                  </div>

                  {!collapsed && (
                    <div className="divide-y divide-gray-100">
                      {entries.map((p) => (
                        <div key={p.id} className="px-3 py-1.5 flex items-center gap-3 text-sm">
                          {p.already_added ? (
                            <span className="h-4 w-4 shrink-0" />
                          ) : (
                            <SelectBox
                              checked={selected.has(p.id)}
                              onChange={(on) => setMany([p.id], on)}
                              label={`Select ${p.name}`}
                            />
                          )}
                          {(p.images?.[0] || p.image_url) ? (
                            <img
                              src={`${API_ORIGIN}${p.images?.[0] || p.image_url}`}
                              alt=""
                              className="w-8 h-8 rounded object-cover border shrink-0"
                            />
                          ) : (
                            <span className="w-8 h-8 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-800 truncate">{p.name}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {p.shop_name}
                              {p.subcategory ? ` · ${p.subcategory}` : ""}
                              {p.brand ? ` · ${p.brand}` : ""}
                            </p>
                          </div>
                          {p.already_added ? (
                            <span className="shrink-0 text-xs text-gray-400">Added</span>
                          ) : (
                            <button
                              onClick={() => add([p.id])}
                              disabled={busy || loading}
                              className="shrink-0 text-primary-700 hover:underline disabled:text-gray-400"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {results.length === 0 && (
              <p className="px-3 py-6 text-center text-gray-400 text-sm">
                No sellable products match — house recipes are excluded.
              </p>
            )}
          </div>
          {results.length >= RESULT_LIMIT && (
            <p className="text-xs text-amber-700 mt-2">
              Showing the first {RESULT_LIMIT} — narrow the search, category or shop to see the rest.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
